import React, { useState, useEffect } from 'react';
import { Check, ChevronLeft, ChevronRight, Info, HelpCircle, X, Loader2 } from 'lucide-react';
import { TerminalRequestFormStep4 } from './TerminalRequestFormStep4';

interface TerminalRequestFormProps {
  form: any;
  handleChange: (e: any) => void;
  handleSubmit: (e: React.FormEvent) => void;
  onSaveAndExit: () => void;
  terminalModel: string;
  BRANDS: any[];
  BRAND_ICONS: any;
}

const getSteps = (hasTerminal: boolean) => {
  const allSteps = [
    { id: 'business', label: 'פרטי עסק' },
    { id: 'contact', label: 'איש קשר' },
    { id: 'shipping', label: 'משלוח' },
    { id: 'terminal', label: 'המסוף' },
    { id: 'brands', label: 'מותגי סליקה' }
  ];

  if (hasTerminal) {
    return allSteps.filter(step => step.id !== 'shipping' && step.id !== 'terminal');
  }

  return allSteps;
};

export const TerminalRequestForm: React.FC<TerminalRequestFormProps> = ({
  form,
  handleChange,
  handleSubmit,
  onSaveAndExit,
  terminalModel,
  BRANDS,
  BRAND_ICONS
}) => {
  const STEPS = getSteps(form.hasTerminal || false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showBrandsHelp, setShowBrandsHelp] = useState(false);
  const [showProviderHelp, setShowProviderHelp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSaving(true);
      setShowSavedIndicator(true);
      setTimeout(() => {
        setIsSaving(false);
        setTimeout(() => setShowSavedIndicator(false), 2000);
      }, 800);
    }, 1000);
    return () => clearTimeout(timer);
  }, [form]);

  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const canGoNext = () => {
    const currentStepId = STEPS[currentStep]?.id;

    switch (currentStepId) {
      case 'business':
        return form.businessName && form.regType && form.regId && form.street && form.streetNumber && form.city && form.businessCategory;
      case 'contact':
        return form.contactName && form.contactPhone && form.contactEmail;
      case 'shipping':
        return form.shippingFullAddress && form.shippingContactName && form.shippingContactPhone;
      case 'terminal':
        return true;
      case 'brands':
        if (!form.selectedProviders || form.selectedProviders.length === 0) {
          return false;
        }
        const hasAllProviderNumbers = form.selectedProviders.every((provider: string) => {
          return form.providerNumbers && form.providerNumbers[provider];
        });

        const requiredMappings = [];
        if (form.cardMapping?.visa) requiredMappings.push('visa');
        if (form.cardMapping?.mastercard) requiredMappings.push('mastercard');
        if (form.cardMapping?.isracard) requiredMappings.push('isracard');
        if (form.cardMapping?.amex) requiredMappings.push('amex');
        if (form.cardMapping?.diners) requiredMappings.push('diners');

        const hasAllCardMappings = requiredMappings.length === 5;
        return hasAllProviderNumbers && hasAllCardMappings;
      default:
        return false;
    }
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="flex flex-col h-full bg-white rounded-t-3xl overflow-hidden">
      {/* Header with Progress */}
      <div className="bg-gradient-to-b from-blue-50 to-white border-b border-gray-200 shrink-0 sticky top-0 z-10">
        <div className="px-4 py-2.5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[17px] font-bold text-gray-900">טופס חיבור מסוף</h2>
            <button
              onClick={onSaveAndExit}
              className="w-8 h-8 rounded-full bg-white hover:bg-gray-50 shadow-sm flex items-center justify-center text-gray-600 transition-all active:scale-95"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-gray-900">
                שלב {currentStep + 1} מתוך {STEPS.length}
              </span>
              {showSavedIndicator && (
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-50 rounded-lg animate-fadeIn">
                  <Check className="h-3 w-3 text-green-600" />
                  <span className="text-[11px] text-green-700 font-medium">נשמר</span>
                </div>
              )}
            </div>
            <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">{terminalModel}</span>
          </div>

          {/* Progress bar with labels */}
          <div className="space-y-1.5">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out shadow-sm"
                style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-500">
              {STEPS.map((step, idx) => (
                <span
                  key={step.id}
                  className={`transition-colors ${
                    idx <= currentStep ? 'text-blue-600 font-medium' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 pb-24 scroll-smooth"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}
      >
        {/* Step: Business Info */}
        {STEPS[currentStep]?.id === 'business' && (
          <div className="space-y-6 animate-fadeIn pb-8">
            <div className="text-center space-y-2 mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <div className="text-2xl">🏢</div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">פרטי העסק</h2>
              <p className="text-sm text-gray-500">נתחיל עם הפרטים הבסיסיים של העסק שלך</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">שם בית עסק</label>
                <input
                  type="text"
                  name="businessName"
                  value={form.businessName}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="למשל: מסעדת גן עדן"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">צורת התאגדות</label>
                <div className="relative">
                  <select
                    name="regType"
                    value={form.regType}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 appearance-none focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                    required
                  >
                    <option value="">בחר...</option>
                    <option value="חברה בע״מ">חברה בע״מ</option>
                    <option value="עוסק מורשה">עוסק מורשה</option>
                    <option value="עוסק פטור">עוסק פטור</option>
                    <option value="עמותה">עמותה</option>
                    <option value="אחר">אחר</option>
                  </select>
                  <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">מספר ח״פ / ע.מ</label>
                <input
                  type="text"
                  name="regId"
                  value={form.regId}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="123456789"
                  required
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">רחוב</label>
                  <input
                    type="text"
                    name="street"
                    value={form.street}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                    placeholder="הרצל"
                    required
                  />
                </div>
                <div className="w-24">
                  <label className="block text-sm font-medium text-gray-700 mb-2">מספר</label>
                  <input
                    type="text"
                    name="streetNumber"
                    value={form.streetNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 text-center focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                    placeholder="12"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">עיר</label>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="תל אביב"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">תחום פעילות</label>
                <input
                  type="text"
                  name="businessCategory"
                  value={form.businessCategory}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="מסעדה / קמעונאות / שירותים"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step: Contact */}
        {STEPS[currentStep]?.id === 'contact' && (
          <div className="space-y-6 animate-fadeIn pb-8">
            <div className="text-center space-y-2 mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <div className="text-2xl">👤</div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">פרטי איש קשר</h2>
              <p className="text-sm text-gray-500">מי יהיה איש הקשר שלנו מטעם העסק?</p>
            </div>

            {form.hasTerminal && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">יש לך כבר מסוף</p>
                    <p className="text-sm text-blue-700">
                      מכיוון שיש לך כבר מסוף פיזי, לא נצטרך פרטי משלוח. נתקדם ישירות להגדרת פרטי הסליקה.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">שם מלא</label>
                <input
                  type="text"
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="ישראל ישראלי"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">טלפון נייד</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={form.contactPhone}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="050-1234567"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">כתובת אימייל</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="example@email.com"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step: Shipping */}
        {STEPS[currentStep]?.id === 'shipping' && (
          <div className="space-y-6 animate-fadeIn pb-8">
            <div className="text-center space-y-2 mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <div className="text-2xl">📦</div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">פרטי משלוח</h2>
              <p className="text-sm text-gray-500">לאן נשלח את המסוף?</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">כתובת מלאה למשלוח</label>
                <input
                  type="text"
                  name="shippingFullAddress"
                  value={form.shippingFullAddress}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="הרצל 12, תל אביב"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">שם המקבל</label>
                <input
                  type="text"
                  name="shippingContactName"
                  value={form.shippingContactName}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="ישראל ישראלי"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">טלפון המקבל</label>
                <input
                  type="tel"
                  name="shippingContactPhone"
                  value={form.shippingContactPhone}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  placeholder="050-1234567"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step: Terminal */}
        {STEPS[currentStep]?.id === 'terminal' && (
          <div className="space-y-6 animate-fadeIn pb-8">
            <div className="text-center space-y-2 mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <div className="text-2xl">💳</div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">המסוף שהזמנת</h2>
              <p className="text-sm text-gray-500">פרטי המסוף ששולם עבורו</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-24 h-24 bg-white rounded-xl p-2 shadow-sm flex items-center justify-center">
                  {terminalModel.includes('N6') ? (
                    <img
                      src="https://res.cloudinary.com/drujiiwnt/images/f_auto,q_auto/v1763909441/Wordpress%20Pelecard%20Website/%D7%AA%D7%9E%D7%95%D7%A0%D7%94-%D7%94%D7%99%D7%A8%D7%95-%D7%9E%D7%A1%D7%95%D7%A4%D7%95%D7%9F-N6_566262a6b/%D7%AA%D7%9E%D7%95%D7%A0%D7%94-%D7%94%D7%99%D7%A8%D7%95-%D7%9E%D7%A1%D7%95%D7%A4%D7%95%D7%9F-N6_566262a6b.webp?_i=AA"
                      alt="N6 Terminal"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : terminalModel.includes('A35') || terminalModel.includes('CTP20') ? (
                    <img
                      src="https://custom-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_limit,fl_lossy,h_1440,w_720,f_auto,q_auto/1402119/553221_440863.png"
                      alt="A35 Terminal"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-4xl">💳</div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-blue-900 mb-1">{terminalModel}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      שולם
                    </span>
                  </div>
                  <p className="text-sm text-blue-700">כמות: 1 מסוף</p>
                </div>
              </div>

              <div className="bg-white/80 rounded-xl p-4 border border-blue-200">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">זמן אספקה משוער</p>
                    <p className="text-blue-700">המסוף יישלח אליך תוך 3-10 ימי עסקים לכתובת שתציין</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step: Brands - iOS-like Design */}
        {STEPS[currentStep]?.id === 'brands' && (
          <TerminalRequestFormStep4 form={form} handleChange={handleChange} />
        )}

        {/* OLD STEP 4 - BACKUP */}
        {false && STEPS[currentStep]?.id === 'brands' && (
          <div className="space-y-6 animate-fadeIn pb-8">
            <div className="text-center space-y-2 mb-6">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <div className="text-2xl">💳</div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">חברת סליקה</h2>
              <p className="text-sm text-gray-500">בחר את חברת הסליקה איתה אתה עובד</p>
            </div>

            {/* שלב 1 - בחירת חברות סליקה */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <h3 className="text-base font-bold text-gray-900">בחר חברת סליקה</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">בחר את החברות שדרכן אתה מבצע סליקה (ניתן לבחור יותר מאחת)</p>

              {/* כאל */}
              <label
                className={`block p-3 rounded-xl cursor-pointer transition-all active:scale-[0.98] border ${
                  form.selectedProviders?.includes('כאל')
                    ? 'bg-blue-500 border-blue-500'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-base font-semibold block ${
                      form.selectedProviders?.includes('כאל') ? 'text-white' : 'text-gray-900'
                    }`}>
                      כאל (ויזה כאל)
                    </span>
                    <span className={`text-xs ${
                      form.selectedProviders?.includes('כאל') ? 'text-blue-50' : 'text-gray-500'
                    }`}>
                      תומכת ב-Visa, MasterCard, Isracard, Diners
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    name="selectedProviders"
                    value="כאל"
                    checked={form.selectedProviders?.includes('כאל') || false}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    form.selectedProviders?.includes('כאל')
                      ? 'bg-white'
                      : 'bg-white border-2 border-gray-300'
                  }`}>
                    {form.selectedProviders?.includes('כאל') && (
                      <Check className="w-4 h-4 text-blue-500" strokeWidth={3} />
                    )}
                  </div>
                </div>
              </label>

              {/* MAX */}
              <label
                className={`block p-3 rounded-xl cursor-pointer transition-all active:scale-[0.98] border ${
                  form.selectedProviders?.includes('MAX')
                    ? 'bg-blue-500 border-blue-500'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-base font-semibold block ${
                      form.selectedProviders?.includes('MAX') ? 'text-white' : 'text-gray-900'
                    }`}>
                      MAX (לאומיכארד)
                    </span>
                    <span className={`text-xs ${
                      form.selectedProviders?.includes('MAX') ? 'text-blue-50' : 'text-gray-500'
                    }`}>
                      תומכת ב-Visa, MasterCard, Isracard
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    name="selectedProviders"
                    value="MAX"
                    checked={form.selectedProviders?.includes('MAX') || false}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    form.selectedProviders?.includes('MAX')
                      ? 'bg-white'
                      : 'bg-white border-2 border-gray-300'
                  }`}>
                    {form.selectedProviders?.includes('MAX') && (
                      <Check className="w-4 h-4 text-blue-500" strokeWidth={3} />
                    )}
                  </div>
                </div>
              </label>

              {/* ישראכרט */}
              <label
                className={`block p-3 rounded-xl cursor-pointer transition-all active:scale-[0.98] border ${
                  form.selectedProviders?.includes('ישראכרט')
                    ? 'bg-blue-500 border-blue-500'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-base font-semibold block ${
                      form.selectedProviders?.includes('ישראכרט') ? 'text-white' : 'text-gray-900'
                    }`}>
                      ישראכרט
                    </span>
                    <span className={`text-xs ${
                      form.selectedProviders?.includes('ישראכרט') ? 'text-blue-50' : 'text-gray-500'
                    }`}>
                      תומכת ב-Visa, MasterCard, Isracard, Amex
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    name="selectedProviders"
                    value="ישראכרט"
                    checked={form.selectedProviders?.includes('ישראכרט') || false}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    form.selectedProviders?.includes('ישראכרט')
                      ? 'bg-white'
                      : 'bg-white border-2 border-gray-300'
                  }`}>
                    {form.selectedProviders?.includes('ישראכרט') && (
                      <Check className="w-4 h-4 text-blue-500" strokeWidth={3} />
                    )}
                  </div>
                </div>
              </label>

              {(!form.selectedProviders || form.selectedProviders.length === 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">
                  <p className="text-xs text-amber-800 text-center">
                    <strong>שים לב:</strong> יש לבחור לפחות חברה אחת
                  </p>
                </div>
              )}
            </div>

            {/* שלב 2 - פרטי מספר ספק */}
            {form.selectedProviders && form.selectedProviders.length > 0 && (
              <div className="space-y-3 pt-4 border-t-2 border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <h3 className="text-base font-bold text-gray-900">פרטי מספר ספק</h3>
                </div>

                {form.selectedProviders.includes('כאל') && (
                  <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 space-y-2">
                    <h4 className="font-semibold text-base text-gray-900">כאל (ויזה כאל)</h4>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        מספר ספק / מספר בית עסק <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="providerNumber-כאל"
                        value={form.providerNumbers?.['כאל'] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 transition-all outline-none"
                        placeholder="לדוגמה: 12345678"
                        required
                      />
                      {(!form.providerNumbers?.['כאל']) && (
                        <p className="text-xs text-red-500 mt-1">נדרש למילוי</p>
                      )}
                    </div>

                    <details className="group">
                      <summary className="cursor-pointer text-xs text-blue-600 font-medium hover:text-blue-700 flex items-center gap-1">
                        <HelpCircle className="w-3.5 h-3.5" />
                        לא יודע את המספר?
                      </summary>
                      <div className="mt-2 p-2 bg-white rounded-lg text-xs text-gray-700 space-y-1.5">
                        <p><strong>איפה למצוא:</strong> מופיע על חשבוניות ודוחות הסליקה</p>
                        <p><strong>אם לא נמצא:</strong> פנה לתמיכת בתי עסק של כאל</p>
                      </div>
                    </details>
                  </div>
                )}

                {form.selectedProviders.includes('MAX') && (
                  <div className="bg-green-50 rounded-xl p-3 border border-green-200 space-y-2">
                    <h4 className="font-semibold text-base text-gray-900">MAX (לאומיכארד)</h4>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        מספר ספק / מספר בית עסק <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="providerNumber-MAX"
                        value={form.providerNumbers?.['MAX'] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:border-green-500 focus:ring-1 focus:ring-green-100 transition-all outline-none"
                        placeholder="לדוגמה: 12345678"
                        required
                      />
                      {(!form.providerNumbers?.['MAX']) && (
                        <p className="text-xs text-red-500 mt-1">נדרש למילוי</p>
                      )}
                    </div>

                    <details className="group">
                      <summary className="cursor-pointer text-xs text-green-600 font-medium hover:text-green-700 flex items-center gap-1">
                        <HelpCircle className="w-3.5 h-3.5" />
                        לא יודע את המספר?
                      </summary>
                      <div className="mt-2 p-2 bg-white rounded-lg text-xs text-gray-700 space-y-1.5">
                        <p className="font-semibold text-amber-700">⚠️ אין מספר ספק אחיד ב-MAX</p>
                        <p><strong>איפה למצוא:</strong> מופיע בדוחות או בחשבוניות של MAX</p>
                        <p><strong>אם לא נמצא:</strong> פנה לתמיכת בתי עסק של MAX</p>
                      </div>
                    </details>
                  </div>
                )}

                {form.selectedProviders.includes('ישראכרט') && (
                  <div className="bg-purple-50 rounded-xl p-3 border border-purple-200 space-y-2">
                    <h4 className="font-semibold text-base text-gray-900">ישראכרט</h4>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        מספר בית עסק (Merchant ID) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="providerNumber-ישראכרט"
                        value={form.providerNumbers?.['ישראכרט'] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-100 transition-all outline-none"
                        placeholder="לדוגמה: 12345678"
                        required
                      />
                      {(!form.providerNumbers?.['ישראכרט']) && (
                        <p className="text-xs text-red-500 mt-1">נדרש למילוי</p>
                      )}
                    </div>

                    <details className="group">
                      <summary className="cursor-pointer text-xs text-purple-600 font-medium hover:text-purple-700 flex items-center gap-1">
                        <HelpCircle className="w-3.5 h-3.5" />
                        לא יודע את המספר?
                      </summary>
                      <div className="mt-2 p-2 bg-white rounded-lg text-xs text-gray-700 space-y-1.5">
                        <p><strong>איפה למצוא:</strong> בדוחות זיכויים/חיובים או בחשבוניות</p>
                        <p><strong>אם לא נמצא:</strong> פנה לתמיכת בתי עסק של ישראכרט</p>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            )}

            {/* שלב 3 - מיפוי סוגי כרטיסים */}
            {form.selectedProviders && form.selectedProviders.length > 0 &&
             form.selectedProviders.every((p: string) => form.providerNumbers?.[p]) && (
              <div className="space-y-3 pt-4 border-t-2 border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  <h3 className="text-base font-bold text-gray-900">מיפוי כרטיסים לסולק</h3>
                </div>

                <p className="text-sm text-gray-600 mb-3">
                  בחר מי מסלק כל סוג כרטיס. <strong>לדוגמה:</strong> ויזה → כאל, Amex → ישראכרט
                </p>

                <div className="space-y-2">
                  {/* Visa */}
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Visa</span>
                      <select
                        className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        value={form.cardMapping?.visa || form.selectedProviders[0]}
                        onChange={(e) => {
                          const newMapping = { ...form.cardMapping, visa: e.target.value };
                          handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                        }}
                      >
                        {form.selectedProviders.map((provider: string) => (
                          <option key={provider} value={provider}>{provider}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* MasterCard */}
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">MasterCard</span>
                      <select
                        className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        value={form.cardMapping?.mastercard || form.selectedProviders[0]}
                        onChange={(e) => {
                          const newMapping = { ...form.cardMapping, mastercard: e.target.value };
                          handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                        }}
                      >
                        {form.selectedProviders.map((provider: string) => (
                          <option key={provider} value={provider}>{provider}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Isracard */}
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Isracard</span>
                      <select
                        className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        value={form.cardMapping?.isracard || form.selectedProviders[0]}
                        onChange={(e) => {
                          const newMapping = { ...form.cardMapping, isracard: e.target.value };
                          handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                        }}
                      >
                        {form.selectedProviders.map((provider: string) => (
                          <option key={provider} value={provider}>{provider}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Amex */}
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">American Express</span>
                      <select
                        className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        value={form.cardMapping?.amex || ''}
                        onChange={(e) => {
                          const newMapping = { ...form.cardMapping, amex: e.target.value };
                          handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                        }}
                      >
                        <option value="">לא נתמך</option>
                        {form.selectedProviders.includes('ישראכרט') && (
                          <option value="ישראכרט">ישראכרט</option>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Diners */}
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Diners</span>
                      <select
                        className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        value={form.cardMapping?.diners || ''}
                        onChange={(e) => {
                          const newMapping = { ...form.cardMapping, diners: e.target.value };
                          handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                        }}
                      >
                        <option value="">לא נתמך</option>
                        {form.selectedProviders.includes('כאל') && (
                          <option value="כאל">כאל</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 pb-safe shrink-0 sticky bottom-0 z-10">
        <div className="flex items-center gap-3">
          {currentStep > 0 && (
            <button
              type="button"
              onClick={goBack}
              className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 active:scale-95 transition-all"
              aria-label="חזור"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          {!isLastStep ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext()}
              className={`flex-1 py-3.5 rounded-2xl font-semibold text-base transition-all ${
                canGoNext()
                  ? 'bg-blue-500 text-white active:scale-[0.98] shadow-lg shadow-blue-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              המשך
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e as any);
              }}
              disabled={!canGoNext()}
              className={`flex-1 py-3.5 rounded-2xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                canGoNext()
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white active:scale-[0.98]'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Check className="w-5 h-5" />
              שלח בקשה
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};
